<script>
  import { onMount, getContext } from 'svelte';
  import { stores } from '@sapper/app';

  import debounce from 'lodash/debounce';
  import Swal from 'sweetalert2';

  import { designState } from '../../stores/Designer.js';
  import { konvaStageId, hidePreview } from '../../stores/Preview.js';
  import { productDataStore } from '../../stores/ProductData.js';
  import { teamDetails } from '../../stores/TeamDetails.js';

  import ColoredButton from './ColoredButton.svelte';
  import Modal from '../../components/Modal.svelte';
  import SizeChart from '../../components/Product/SizeChart.svelte';
  import BulkPricingModal from '../../components/BulkPricingModal.svelte';

  export let konvaStage;

  // This component is bound to konva stage.
  // We need to wait for the stage to be ready. Since the stage can only be added after the DOM is ready.

  const defaultBulkDiscounts = getContext('defaultBulkDiscounts');
  const productSpecificBulkDiscounts = []; // TODO - get these from productDataStore? or designer level context

  if ($teamDetails.length === 0) {
    teamDetails.addRow();
  }

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function compareSizesbyGender(a, b) {
    //Youth first
    if (a.ProductSize.gender === 'youth' && b.ProductSize.gender !== 'youth') {
      return -1;
    }
    //Mens last
    if (a.ProductSize.gender === 'mens' && b.ProductSize.gender !== 'mens') {
      return 1;
    }
    if (a.ProductSize.gender !== 'mens' && b.ProductSize.gender === 'mens') {
      return -1;
    }
  }

  function sizeSelected(productType, index, event) {
    //console.log("size selected for: ", productType, index, event.target.value);
    //If user selects none for size - e.g. want top but no shorts
    //We will hide the qty input, but need to reset the qty value to 0
    if (!numberInputRequired && !nameInputRequired && event.target.value === '0') {
      //Reset quantity to 0
      teamDetails.setSizeQty(index, productType, 0, 0);
      $teamDetails = $teamDetails;
    }

    if (event.target.value !== '0' && $teamDetails[index][productType + 'Qty'] === 0) {
      teamDetails.setSizeQty(index, productType, event.target.value, 1);
      $teamDetails = $teamDetails;
    }
  }

  function deleteRow(index) {
    //Do not allow deletion of the first row.
    if (index !== 0) {
      teamDetails.deleteRow(index);
    }
    numberOfTeamMembers = numberOfTeamMembers - 1;
  }

  function validateNumberFunction(event, index) {
    //Allow only 2 digit positive numbers
    let input = event.target.value;
    //let pattern = /^[0-9]{2}$/;
    //let valid = pattern.test(input);
    let sanitisedValue = input.replace(/\D/g, '').substring(0, 2);

    $teamDetails[index].number = sanitisedValue;
  }

  const validateNumber = debounce(validateNumberFunction, 300);

  function validateNameFunction(event, index) {}

  const validateName = debounce(validateNameFunction, 300);

  function validateQtyFunction(event, index, productType) {
    let input = parseInt(event.target.value);
    if (input <= 0) {
      $teamDetails[index][productType + 'Qty'] = null;
    }
  }

  const validateQty = debounce(validateQtyFunction, 300);

  function addRow() {
    teamDetails.addRow();
  }

  async function qtySelect() {
    let currentLength = $teamDetails.length;
    if (numberOfTeamMembers < currentLength) {
      const result = await Swal.fire({
        title: 'Delete Items',
        html: 'You have selected a quantity that is less than the number of items already entered.<br>If you continue, the excess items will be deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete items!',
      });

      if (result.isConfirmed) {
        teamDetails.deleteRowsAfterIndex(numberOfTeamMembers - 1);
      } else {
        numberOfTeamMembers = currentLength;
      }
    } else {
      for (let i = currentLength; i < numberOfTeamMembers; i++) {
        addRow();
      }
    }
  }

  let teamGrid = true;
  function determineGridLayout() {
    let gridTemplateColumns = '30px';
    if (!numberInputRequired && !nameInputRequired) {
      //Setup different grid for size/qqty input
      teamGrid = false;
      //Reset names and numbers to blank in teamDetails store
      for (let i = 0; i < $teamDetails.length; i++) {
        $teamDetails[i].name = null;
        $teamDetails[i].number = null;
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('top')) {
        gridTemplateColumns += ' 135px 80px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('bottom')) {
        gridTemplateColumns += ' 135px 80px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('hat')) {
        gridTemplateColumns += ' 135px 80px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('socks')) {
        gridTemplateColumns += ' 135px 80px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('accessory')) {
        gridTemplateColumns += ' 135px 80px';
      }
      //Delete button
      gridTemplateColumns += ' 50px';
    } else {
      teamGrid = true;
      //Reset qty values to null in teamDetails store
      for (let i = 0; i < $teamDetails.length; i++) {
        $teamDetails[i].topQty = null;
        $teamDetails[i].bottomQty = null;
        $teamDetails[i].hatQty = null;
        $teamDetails[i].socksQty = null;
        $teamDetails[i].accessoryQty = null;
      }
      //team member based grid
      if (nameInputRequired) {
        gridTemplateColumns += ' 140px';
      }
      if (numberInputRequired) {
        gridTemplateColumns += ' 80px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('top')) {
        gridTemplateColumns += ' 135px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('bottom')) {
        gridTemplateColumns += ' 135px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('hat')) {
        gridTemplateColumns += ' 135px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('socks')) {
        gridTemplateColumns += ' 135px';
      }
      if (arrayOfSelectedProducts && arrayOfSelectedProducts.includes('accessory')) {
        gridTemplateColumns += ' 135px';
      }
      //Delete button
      gridTemplateColumns += ' 50px';
    }

    return gridTemplateColumns;
  }

  const { page } = stores();
  let teamDetailsValid = true;
  if ($page?.query?.valid === 'false') {
    teamDetailsValid = false;
  }

  let arrayOfSelectedProducts;
  let numberInputRequired = false;
  let nameInputRequired = false;
  let invalidRows = [];

  let numberOfTeamMembers = $teamDetails.length;

  let showModal = false;
  let showPricingModal = false;

  // Need to wait for stage to be ready to get ID - Only if we want to support multiple designs?
  // TODO - Could just get ID from saved state on mount?
  $: if (konvaStage) {
    arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId); // Once we have the stage the id store will be set.
    $hidePreview = true; // Once the stage has loaded we can hide the preview
  }

  $: if (arrayOfSelectedProducts && teamDetailsValid === false) {
    invalidRows = teamDetails.getInvalidRows(arrayOfSelectedProducts, numberInputRequired, nameInputRequired);
  }

  // Make a reactive statement to set invalidRows
  $: if ($teamDetails && arrayOfSelectedProducts) {
    invalidRows = teamDetails.getInvalidRows(arrayOfSelectedProducts, numberInputRequired, nameInputRequired);
  }

  let productSizeData = {
    topSizes: null,
    bottomSizes: null,
    hatSizes: null,
    socksSizes: null,
    accessorySizes: null,
  };

  let productGenderData = {
    topGenders: null,
    bottomGenders: null,
    hatGenders: null,
    socksGenders: null,
    accessoryGenders: null,
  };

  // Need to wait for array to be loaded. Only run once
  let sizeAndGenderSetupComplete = false;
  $: if (arrayOfSelectedProducts && !sizeAndGenderSetupComplete) {
    // Once array of products is available check if we need number input
    numberInputRequired = designState.isNumberInputRequired($konvaStageId, arrayOfSelectedProducts);
    nameInputRequired = designState.isNameInputRequired($konvaStageId, arrayOfSelectedProducts);

    // Calculate grid template columns here as we have all details
    gridTemplateColumns = determineGridLayout();

    // TODO- if name and number are not required switch to a qty size entry form

    for (const product of arrayOfSelectedProducts) {
      //Check existence of product - if we load directly to this page with new product then might fail
      if ($productDataStore[product]) {
        ////console.log("product in data store: ", $productDataStore[product]);
        let sizes = $productDataStore[product].sizes;
        //check selected product type colour genders
        let gendersAvailable = {
          mens: $productDataStore[product].selectedColour.mens,
          womens: $productDataStore[product].selectedColour.womens,
          youth: $productDataStore[product].selectedColour.youth,
        };

        productGenderData[product + 'Genders'] = gendersAvailable;

        // sort the sizes by gender
        sizes.sort(compareSizesbyGender);

        // Remove sizes for genders that are not available
        let filteredSizes = sizes.filter((size) => {
          if (gendersAvailable[size.ProductSize.gender]) {
            return true;
          }
        });
        productSizeData[product + 'Sizes'] = filteredSizes;
      }
    }
    sizeAndGenderSetupComplete = true;
  }

  let gridTemplateColumns;
  onMount(() => {
    // Rework sizes and gender when component remounts
    sizeAndGenderSetupComplete = false;
  });
</script>

{#if konvaStage}
  <Modal bind:show={showModal} widthLg={true}>
    <div class="size-guide">
      <h3>Size Guide and Chart</h3>
      <img
        src="https://images.uniformhero.com.au/assets/web/size-guide.jpg"
        alt="guide on how to measure half chest sizes"
      />
      <div class="size-guide-text">
        Take an existing shirt that fits you well, lay it flat on a table and measure the half-chest (5cm under the
        armpit). Then check that measurement against the product sizes on our website to select the best size for your
        item.
      </div>
    </div>
    {#if arrayOfSelectedProducts}
      {#each arrayOfSelectedProducts as product}
        <div class="sizes">
          {#if productGenderData[product + 'Genders'].mens}
            <SizeChart
              sizes={productSizeData[product + 'Sizes']}
              genderToShow="mens"
              productName={$productDataStore?.[product].name}
            />
          {/if}
          {#if productGenderData[product + 'Genders'].womens}
            <SizeChart
              sizes={productSizeData[product + 'Sizes']}
              genderToShow="womens"
              productName={$productDataStore?.[product].name}
            />
          {/if}
          {#if productGenderData[product + 'Genders'].youth}
            <SizeChart
              sizes={productSizeData[product + 'Sizes']}
              genderToShow="youth"
              productName={$productDataStore?.[product].name}
            />
          {/if}
        </div>
      {/each}
    {/if}
  </Modal>

  {#if showPricingModal}
    <BulkPricingModal bind:show={showPricingModal} {arrayOfSelectedProducts} />
  {/if}

  {#if teamGrid}
    <div class="dropdown-container">
      <div class="label">How many team members do you have?</div>
      <div class="qty-input">
        <select name="qty" id="" bind:value={numberOfTeamMembers} on:change={qtySelect}>
          {#each { length: 50 } as _, i}
            <option value={i + 1}>{i + 1}</option>
          {/each}
        </select>
      </div>
    </div>
  {/if}

  <div class="table-container">
    {#if !teamDetailsValid && invalidRows.length > 0}
      <div class="invalid-message">Please update the highlighted rows</div>
    {/if}

    <!-- Heading Row -->
    <div class="row header" style="grid-template-columns:{gridTemplateColumns};">
      <div class="index" />
      {#if nameInputRequired}
        <div class="name row-heading">Name</div>
      {/if}
      {#if numberInputRequired}
        <div class="number row-heading">Number</div>
      {/if}

      {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('top')}
        <div class="size row-heading">Top Size</div>
      {/if}
      {#if !teamGrid}
        <div class="qty row-heading">Qty</div>
      {/if}
      {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('bottom')}
        <div class="size row-heading">Bottom Size</div>
        {#if !teamGrid}
          <div class="qty row-heading">Qty</div>
        {/if}
      {/if}
      {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('hat')}
        <div class="size row-heading">Hat Size</div>
        {#if !teamGrid}
          <div class="qty row-heading">Qty</div>
        {/if}
      {/if}
      {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('socks')}
        <div class="size row-heading">Socks Size</div>
        {#if !teamGrid}
          <div class="qty row-heading">Qty</div>
        {/if}
      {/if}
      {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('accessory')}
        <div class="size row-heading">Accessory Size</div>
        {#if !teamGrid}
          <div class="qty row-heading">Qty</div>
        {/if}
      {/if}
      <div class="delete" />
    </div>

    <!-- Table Rows -->
    {#each $teamDetails as item, index}
      <div class="row" class:invalid={invalidRows.includes(index)} style="grid-template-columns:{gridTemplateColumns};">
        <div class="index">{index + 1}.</div>
        {#if nameInputRequired}
          <div class="name">
            <input type="text" bind:value={$teamDetails[index].name} on:input={(e) => validateName(e, index)} />
          </div>
        {/if}
        {#if numberInputRequired}
          <div class="number">
            <input
              class="number-input"
              type="text"
              bind:value={$teamDetails[index].number}
              on:input={(e) => validateNumber(e, index)}
            />
          </div>
        {/if}

        {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('top')}
          <div class="size">
            <select
              name="top-sizes"
              id="top-sizes"
              on:change={(e) => sizeSelected('top', index, e)}
              bind:value={$teamDetails[index].topSizeId}
              required
            >
              <option value={0}>None</option>
              {#if productSizeData.topSizes}
                {#each productSizeData.topSizes as size}
                  <option value={size.ProductSize.id}
                    >{capitalizeFirstLetter(size.ProductSize.gender)}
                    {size.ProductSize.name.toUpperCase()}</option
                  >
                {/each}
              {/if}
            </select>
          </div>
          {#if !teamGrid}
            <div class="size-qty-input">
              {#if $teamDetails[index].topSizeId !== 0}
                <input
                  class="number-input"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={$teamDetails[index].topQty}
                  on:input={(e) => validateQty(e, index, 'top')}
                />
              {/if}
            </div>
          {/if}
        {/if}

        {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('bottom')}
          <div class="size">
            <select
              name="bottom-sizes"
              id="bottom-sizes"
              on:change={(e) => sizeSelected('bottom', index, e)}
              bind:value={$teamDetails[index].bottomSizeId}
              required
            >
              <option value={0}>None</option>
              {#if productSizeData.bottomSizes}
                {#each productSizeData.bottomSizes as size}
                  <option value={size.ProductSize.id}
                    >{capitalizeFirstLetter(size.ProductSize.gender)}
                    {size.ProductSize.name.toUpperCase()}</option
                  >
                {/each}
              {/if}
            </select>
          </div>
          {#if !teamGrid}
            <div class="size-qty-input">
              {#if $teamDetails[index].bottomSizeId !== 0}
                <input
                  class="number-input"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={$teamDetails[index].bottomQty}
                  on:input={(e) => validateQty(e, index, 'bottom')}
                />
              {/if}
            </div>
          {/if}
        {/if}

        {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('hat')}
          <div class="size">
            <select
              name="hat-sizes"
              id="hat-sizes"
              on:change={(e) => sizeSelected('hat', index, e)}
              bind:value={$teamDetails[index].hatSizeId}
              required
            >
              <option value={0}>None</option>
              {#if productSizeData.hatSizes}
                {#each productSizeData.hatSizes as size}
                  <option value={size.ProductSize.id}
                    >{capitalizeFirstLetter(size.ProductSize.gender)}
                    {size.ProductSize.name.toUpperCase()}</option
                  >
                {/each}
              {/if}
            </select>
          </div>
          {#if !teamGrid}
            <div class="size-qty-input">
              {#if $teamDetails[index].hatSizeId !== 0}
                <input
                  class="number-input"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={$teamDetails[index].hatQty}
                  on:input={(e) => validateQty(e, index, 'hat')}
                />
              {/if}
            </div>
          {/if}
        {/if}

        {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('socks')}
          <div class="size">
            <select
              name="socks-sizes"
              id="socks-sizes"
              on:change={(e) => sizeSelected('socks', index, e)}
              bind:value={$teamDetails[index].socksSizeId}
              required
            >
              <option value={0}>None</option>
              {#if productSizeData.socksSizes}
                {#each productSizeData.socksSizes as size}
                  <option value={size.ProductSize.id}
                    >{capitalizeFirstLetter(size.ProductSize.gender)}
                    {size.ProductSize.name.toUpperCase()}</option
                  >
                {/each}
              {/if}
            </select>
          </div>
          {#if !teamGrid}
            <div class="size-qty-input">
              {#if $teamDetails[index].socksSizeId !== 0}
                <input
                  class="number-input"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={$teamDetails[index].socksQty}
                  on:input={(e) => validateQty(e, index, 'socks')}
                />
              {/if}
            </div>
          {/if}
        {/if}

        {#if arrayOfSelectedProducts && arrayOfSelectedProducts.includes('accessory')}
          <div class="size">
            <select
              name="accessory-sizes"
              id="accessory-sizes"
              on:change={(e) => sizeSelected('accessory', index, e)}
              bind:value={$teamDetails[index].accessorySizeId}
              required
            >
              <option value={0}>None</option>
              {#if productSizeData.accessorySizes}
                {#each productSizeData.accessorySizes as size}
                  <option value={size.ProductSize.id}
                    >{capitalizeFirstLetter(size.ProductSize.gender)}
                    {size.ProductSize.name.toUpperCase()}</option
                  >
                {/each}
              {/if}
            </select>
          </div>
          {#if !teamGrid}
            <div class="size-qty-input">
              {#if $teamDetails[index].accessorySizeId !== 0}
                <input
                  class="number-input"
                  type="number"
                  min="1"
                  step="1"
                  bind:value={$teamDetails[index].accessoryQty}
                  on:input={(e) => validateQty(e, index, 'accessory')}
                />
              {/if}
            </div>
          {/if}
        {/if}
        {#if index > 0}
          <div class="delete">
            <ColoredButton label={'X'} type={'red'} clickFn={() => deleteRow(index)} />
          </div>
        {/if}
      </div>
    {/each}

    <div class="add-row">
      <ColoredButton label={'Add Row'} type={'green'} clickFn={addRow} />
    </div>

    <div class="size-chart-container">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div on:click={() => (showModal = !showModal)}>View size chart</div>
    </div>
    <div class="pricing-details-container">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div on:click={() => (showPricingModal = !showPricingModal)}>View pricing details</div>
    </div>
    <div class="print-info-container">
      NOTE: The names and numbers entered here will be printed on the jersey. Please ensure they are correct.
    </div>
  </div>
{/if}

<style>
  .dropdown-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2rem;
  }
  .qty-input {
    margin-left: 2rem;
  }

  .table-container {
    display: table;
    margin-left: auto;
    margin-right: auto;
    padding-top: 2rem;
    padding-bottom: 50px;
  }

  .invalid-message {
    text-align: center;
    font-weight: bold;
    text-transform: uppercase;
    color: #fe5900;
    letter-spacing: 1.1px;
  }

  .row {
    padding-left: 1rem;
    padding-right: 1rem;
    display: grid;
    grid-template-rows: 50px;
    grid-gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .row > * {
    padding: 1rem;
  }

  .row.invalid {
    background-color: #feab7f;
    border-radius: 1rem;
  }

  /* .row.header {
    grid-template-rows: 30px;
  } */

  .index,
  .name,
  .number,
  .qty,
  .size-qty-input,
  .size,
  .delete {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .size {
    text-transform: capitalize;
  }

  .row-heading {
    font-weight: bold;
    color: #666;
    letter-spacing: 1.5px;
    font-size: 1.2rem;
    text-transform: uppercase;
  }

  select,
  input {
    padding: 1rem;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    border-radius: 0.5rem;
    border: 1px solid #777;
    max-width: 100%;
  }

  input.number-input {
    text-align: center;
  }

  .delete {
    cursor: pointer;
  }

  .add-row {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }

  .sizes {
    margin: 6rem auto;
    max-width: 100rem;
    width: 95%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 4rem;
  }

  .size-guide {
    display: flex;
    flex-direction: column;
    margin-bottom: 2rem;
    margin-left: 4rem;
    margin-right: 4rem;
    align-items: center;
    gap: 2rem;
  }

  .size-guide h3 {
    text-transform: uppercase;
    font-family: 'Oswald';
    color: #fe5900;
  }

  .size-guide img {
    width: 250px;
  }

  .size-chart-container {
    text-align: center;
    margin-top: 2rem;
    text-transform: uppercase;
    font-family: 'Oswald';
    color: #fe5900;
    cursor: pointer;
  }

  .pricing-details-container {
    text-align: center;
    margin-top: 1rem;
    text-transform: uppercase;
    font-family: 'Oswald';
    color: #fe5900;
    cursor: pointer;
  }

  .print-info-container {
    margin-top: 2rem;
    font-size: 1.2rem;
    color: #666;
  }

  .inclusions {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 2.4rem;
  }
</style>
